import React, { FC } from 'react';
import { useAccount } from './useAccount';
import { PERMISSIONS } from 'constants/permissions';
import { Avatar, Button, Col, FMGModal, Popconfirm, Row, Select, Typography, Upload } from 'components';
import { useAccessControl } from 'components/common/accessControl/useAccessControl';
import { ActivateIcon, DeactivateIcon, DeleteIcon, ResetPassIcon } from 'components/common/icons';
import styles from './styles.module.scss';
import { Form } from 'antd';

export const UserAccount: FC = () => {
  const { hasAccess } = useAccessControl();
  const {
    contextHolder,
    isUserActive,
    isDeactivateVisible,
    handleAvatarChange,
    userDetails,
    handleAvatarDelete,
    activateAccount,
    deactivateAccount,
    resetPassword,
    isLoadingReset,
    setOpenResetPassword,
    openResetPassword,
    openActivateDialog,
    setOpenActivateDialog,
    accountActivationLoading,
    form,
    filteredUserList,
  } = useAccount();
  return (
    <>
      {contextHolder}
      <Row className={styles.account} gutter={[20, 0]}>
        <Col flex={'120px'}>
          <Avatar src={userDetails?.files?.AVATAR} className={styles.avatar} size="large">
            {userDetails?.files?.AVATAR || (
              <>
                {userDetails.firstName?.charAt(0)}
                {userDetails.lastName?.charAt(0)}
              </>
            )}
          </Avatar>
          {userDetails?.files?.AVATAR && hasAccess(PERMISSIONS.users.account.write) && (
            <Popconfirm
              title="Are you sure you want to delete profile picture?"
              onConfirm={handleAvatarDelete}
              okText="Yes"
              cancelText="No"
            >
              <Button shape="circle" icon={<DeleteIcon />} size="small" className={styles.avatar_delete_btn} />
            </Popconfirm>
          )}
        </Col>
        <Col flex={'600px'}>
          <Row justify={'start'} className={styles.content} gutter={[0, 32]}>
            <Col span={24}>
              <Typography className={styles.label}>
                {userDetails.firstName} {userDetails.lastName}
              </Typography>
              <Typography className={styles.description}>{userDetails.title}</Typography>
              {hasAccess(PERMISSIONS.users.user.write) && (
                <Upload
                  showUploadList={false}
                  beforeUpload={() => false} // Prevent automatic upload
                  onChange={handleAvatarChange}
                  accept={'.jpg,.jpeg,.png'}
                >
                  <Button className={styles.btn_primary} variant={'outlined'}>
                    Upload Image
                  </Button>
                </Upload>
              )}
            </Col>
            {hasAccess(PERMISSIONS.users.reset_password.write) && (
              <Col span={24}>
                <Typography className={styles.label}>Reset Password</Typography>
                <Typography className={styles.description}>
                  {`Reset the user's password to allow them to set a new one.`}
                </Typography>
                <Button onClick={() => setOpenResetPassword(true)} className={styles.btn_primary} variant={'outlined'}>
                  Reset Password
                </Button>
              </Col>
            )}
            {hasAccess(PERMISSIONS.users.account_status.write) && isDeactivateVisible && (
              <Col span={24}>
                <Typography className={styles.label}>{isUserActive ? 'Deactivate' : 'Reactivate'} Account</Typography>
                <Typography className={styles.description}>
                  {isUserActive ? (
                    <>
                      {`Temporarily deactivate the user's account. This action `}
                      <br />
                      will disable access to the system until reactivated.
                    </>
                  ) : (
                    <>
                      Reactivating will restore access to system features and services
                      <br />
                      according to the assigned roles.
                    </>
                  )}
                </Typography>
                <Button
                  onClick={() => setOpenActivateDialog(true)}
                  className={isUserActive ? styles.btn_danger : styles.btn_primary}
                  variant={'outlined'}
                >
                  {isUserActive ? 'Deactivate' : 'Reactivate'} Account
                </Button>
              </Col>
            )}
          </Row>
        </Col>
        <FMGModal
          okText={'Reset'}
          width={360}
          open={openResetPassword}
          title={''}
          isLoading={isLoadingReset}
          onSubmit={resetPassword}
          onCancel={() => setOpenResetPassword(false)}
        >
          <Row justify={'center'}>
            <Col style={{ textAlign: 'center', margin: 15 }} span={24}>
              <ResetPassIcon />
            </Col>
            <Typography className={styles.label}>Reset Password</Typography>
            <Typography
              style={{ textAlign: 'center', marginTop: 12 }}
              className={styles.description}
            >{`Are you sure you want to reset user's password`}</Typography>
          </Row>
        </FMGModal>
        <FMGModal
          okText={'Reset'}
          width={360}
          open={openResetPassword}
          title={''}
          isLoading={isLoadingReset}
          onSubmit={resetPassword}
          onCancel={() => setOpenResetPassword(false)}
        >
          <Row justify={'center'}>
            <Col style={{ textAlign: 'center', margin: 15 }} span={24}>
              <ResetPassIcon />
            </Col>
            <Typography className={styles.label}>Reset Password</Typography>
            <Typography
              style={{ textAlign: 'center', marginTop: 12 }}
              className={styles.description}
            >{`Are you sure you want to reset user's password`}</Typography>
          </Row>
        </FMGModal>
        <FMGModal
          okText={isUserActive ? 'Deactivate' : 'Reactivate'}
          width={360}
          open={openActivateDialog}
          isLoading={accountActivationLoading}
          onSubmit={isUserActive ? deactivateAccount : activateAccount}
          obBtnDanger={isUserActive}
          onCancel={() => setOpenActivateDialog(false)}
        >
          <Form layout={'vertical'} form={form} requiredMark={false}>
            <Row justify={'center'}>
              <Col style={{ textAlign: 'center', margin: 15 }} span={24}>
                {isUserActive ? <DeactivateIcon /> : <ActivateIcon />}
              </Col>
              <Typography color={''} className={styles.label}>
                {isUserActive ? 'Deactivate' : 'Reactivate'} Account
              </Typography>
              <Typography style={{ textAlign: 'center', marginTop: 12 }} className={styles.description}>
                {isUserActive
                  ? `Deactivation will disable access to all system features, but the account can be reactivated later. Please select employee for replacement.`
                  : `Are you sure you want to reactivate this user account? Reactivating will restore access to system features and services according to the assigned roles.`}
              </Typography>
              {isUserActive && (
                <Col span={24} className={'margin-t-12'}>
                  <Form.Item label={'Replacement'} name={'replacementId'}>
                    <Select options={filteredUserList} size="large" />
                  </Form.Item>
                </Col>
              )}
            </Row>
          </Form>
        </FMGModal>
      </Row>
    </>
  );
};
